/* eslint-disable camelcase */

import { SpecialistRole, SpecialistType } from 'services/specialist';

import { PreAssessmentAnswerV2 } from 'pages/PreAssessment/types';

import { PageTypeEnum } from '..';

export interface Topic {
  id: number;
  titleKey: string;
  delivered: boolean;
}

export enum PaymentModel {
  CREDIT = 'credit',
  SUBSCRIPTION = 'subscription',
}

export interface EndUserState {
  email: string;
  gender: string;
  userID: string;
  cognitoID?: string;
  companyID?: string;
  fullName: string;
  birthdate: string;
  phoneNumber: string;
  criticality: {
    has_shown_critical_signs: boolean;
    is_criticality_confirmed: boolean;
    confirmed_at: string;
    confirmed_with_ext_user_present: boolean;
  };
  company_ext_referral_info?: {
    has_ext_users: boolean;
  };
  is_critical: boolean;
  showLogbook: boolean;
  patient_intake: {
    answer: string;
  };
  care_profile?: string;
  company_name: string;
  sessions: {
    completed: number;
    completed_with_me: number;
    remaining: number;
  };
  link_created_at: string;
  user_expiration_date?: string;
  patient_record: {
    must_update: boolean;
  };
  link: {
    link_source: string;
    previous_type: string;
  };
  payment_model: PaymentModel;
}

export interface EndUserSessionsState {
  session?: number;
  start_at: Date;
  status: string;
}

export interface EndUserSessionsResponse {
  data: EndUserSessionsState[];
}

export interface EndUserNotesResponse {
  id: string;
  user_id: string;
  specialist_id: string;
  title: string;
  note: string;
  created_at: Date;
  updated_at: Date;
}

export interface EndUserLogbookResponse {
  id: string;
  user_id: string;
  specialist_id: string;
  title: string;
  logbook: string;
  created_at: Date;
  updated_at: Date;
}

export interface EndUserNotesStates {
  cognito_user_id: string;
  title: string;
  note: string;
}

export interface EndUserLogbookStates extends Omit<EndUserNotesStates, 'note'> {
  description: string;
}

export type ClinicalAssessmentResult =
  | 'lbl-none-minimal'
  | 'lbl-mild'
  | 'lbl-moderate'
  | 'lbl-moderately-severe'
  | 'lbl-severe'
  | 'lbl-minimal-anxiety'
  | 'lbl-mild-anxiety'
  | 'lbl-moderate-anxiety'
  | 'lbl-severe-anxiety'
  | 'high'
  | 'very-high'
  | 'low'
  | 'very-low';

export type LinkReason =
  | 'user_discharged_by_cx'
  | 'user_discharged_by_cp'
  | 'user_transferred_by_tc'
  | 'user_selected_TC_journey'
  | 'user_selected_tc_journey'
  | 'user_unlinked_by_cp_layoff'
  | 'user_did_not_selected_journey'
  | 'user_selected_cp_journey_no_booked_session'
  | 'user_not_satisfied_with_cp_suggestions'
  | 'user_potentially_critical_not_satisfied_with_cp_suggestions'
  | 'user_with_accesssibility_needs'
  | 'user_sent_message_tc_auto_journey'
  | 'user_did_not_send_message_tc_auto_journey'
  | 'user_sent_message_tc_journey'
  | 'user_did_not_send_message_tc_journey';

export type DischargeReason =
  | 'new_link'
  | 'inactive'
  | 'eligibility_shutdown'
  | 'company_shutdown'
  | 'user_deleted'
  | 'forward'; // Note: forward reason is not being send yet by BE

export interface ClinicalAssessment {
  value: number;
  submission_id: string;
  result: ClinicalAssessmentResult;
  updated_at: string;
}

export enum ScoreType {
  GAD = 'gad',
  PHQ = 'phq',
  WHO = 'who',
}

export interface AssessmentsResult {
  type: ScoreType;
  data: ClinicalAssessment[];
}

export interface Answer {
  question: string;
  description: string;
  answer: string;
}

export interface AssessmentAnswerV2 {
  answers: Answer[];
  metadata: {
    form_type: string;
  };
}

export interface PreAssessmentFieldOption {
  title: string;
  value: string;
}

export interface PreAssessmentField {
  value: string;
  field: {
    title: string;
    description: string;
    options: PreAssessmentFieldOption[];
  };
}

export interface PreAssessmentResult {
  listSubmissions: {
    entries: [
      {
        fields: PreAssessmentField[];
      },
    ];
  };
}

export interface PagingMetadata {
  pages: number;
  nextCursor: string;
}

export interface ActivitiesProgress {
  total: number;
  acknowledged: number;
}

export interface ActivitiesTopic {
  id: number;
  titleKey: string;
  program: string;
  programId: number;
  deliverDate: string;
  progress: ActivitiesProgress;
}

export interface ActivitiesTopicsResponse {
  metadata: PagingMetadata;
  results: ActivitiesTopic[];
}

export interface TopicsPage {
  id: string;
  title: string;
  acknowledge: boolean;
  acknowledge_date?: string;
  external_form_id: string;
  type: PageTypeEnum | string;
  read_time: string;
  duration: string;
  cover?: string;
  audio_file?: unknown;
  tags?: string[];
  program_id?: string;
}

export interface AssessmentAnswersResponse {
  url: string;
}

export interface ProgramListResponse {
  id: number;
  titleKey: string;
  delivered: boolean;
  topics: Topic[];
}

export interface RecordQuestion {
  id: string;
  date?: string;
  content?: string;
  title_key: string;
  specialist_name: string;
  field_description_key: string;
  allowed_roles: SpecialistRole[];
  specialist_type: SpecialistType;
  tooltip_description_key?: string;
}

export interface PatientRecordsId {
  id: string;
}

export interface PatientRecords extends PatientRecordsId {
  questions: RecordQuestion[];
}

export interface PatientRecordEntry extends RecordQuestion {
  tooltip_description_key: string;
}

export interface PatientRecordEntryResponse {
  results: PatientRecordEntry[];
}

export interface CreatePatientRecordsEntries {
  userCognitoId: string;
  patientRecordId: string;
  questionId: string;
  content: string;
}

export interface PreAssessmentQuestions {
  questions: PreAssessmentAnswerV2[];
}

export type WorkflowType = 'onboarding';

export type EndUserProgramsTopic = {
  id: number;
  titleKey: string;
  program: string;
  programId: number;
  deliverDate: number;
  progress: {
    total: number;
    acknowledged: number;
  };
};

export type GetEndUserProgramsResponse = {
  results: EndUserProgramsTopic[];
};

export type CareProgramTopic = {
  id: number;
  title_key: string;
  external_task_execution_id: number;
  status: string;
};

export type CareProgram = {
  id: string;
  title_key: string;
  status: string;
  has_next: boolean;
  created_at: string;
  topics: CareProgramTopic[];
};
